import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	deleteTopicAttachment,
	getCategories,
	getCompletedOffers,
	getCountriesByIds,
	getCropsByIds,
	getFarmerDataById,
	getGuideline,
	getOffers,
	getOffersSpecialist,
	getSpecialistsByIds,
	getTopicAttachments,
} from './monitoringOffersService';
import { useMonitoringOffersStore } from './monitoringOffersStore';

type Slug = 'request-and-offer' | 'ongoing-project';

export function useGetOffers(slug?: Slug) {
	const { setOngoingOffers } = useMonitoringOffersStore();
	const [data, setData] = useState<any>(null);
	const [error, setError] = useState<any>(null);
	const [isLoading, setIsLoading] = useState(true);
	const isFetchingRef = useRef(false);

	const fetchOffers = useCallback(async () => {
		if (!slug) {
			setIsLoading(false);
			return;
		}

		// Prevent duplicate fetches
		if (isFetchingRef.current) {
			return;
		}

		isFetchingRef.current = true;
		setIsLoading(true);

		try {
			const offers = await getOffers(slug);

			if (!offers?.data) {
				setData(offers);
				setIsLoading(false);
				isFetchingRef.current = false;
				return;
			}

			// Don't update store yet - wait for all related data to be fetched
			const rawOffers = offers.data;

			const cropIds = [
				...new Set(
					rawOffers
						.map((offer: { crop_id?: string }) => offer.crop_id)
						.filter((id: any): id is string => typeof id === 'string')
				),
			];

			const specialistIds = [
				...new Set(
					rawOffers.flatMap((offer: { specialist_id?: string | string[] }) => {
						// Handle both array and string cases
						if (Array.isArray(offer.specialist_id)) {
							return offer.specialist_id;
						} else if (typeof offer.specialist_id === 'string') {
							return [offer.specialist_id];
						}
						return [];
					})
				),
			];

			try {
				const [crops, specialists] = await Promise.all([
					cropIds.length ? getCropsByIds(cropIds) : Promise.resolve([]),
					specialistIds.length ? getSpecialistsByIds(specialistIds) : Promise.resolve([]),
				]);

				const cropsMap = new Map(crops.map((crop: any) => [crop.id, crop]));
				const specialistsMap = new Map(specialists.map((s: any) => [s.user_id, s]));

				const countryIds = [
					...new Set(
						specialists
							.map((s: any) => s.country)
							.filter((id: any): id is string => typeof id === 'string')
					),
				];

				const countries = countryIds.length ? await getCountriesByIds(countryIds) : [];
				const countriesMap = new Map(countries.map((c: any) => [c.id, c]));

				const enrichedSpecialists = specialists.map((specialist: any) => ({
					...specialist,
					country: countriesMap.get(specialist.country) || null,
				}));

				enrichedSpecialists.forEach((s: any) => specialistsMap.set(s.user_id, s));

				const enrichedOffers = rawOffers.map((offer: any) => {
					const specialist = specialistsMap.get(offer.selected_specialist_id) || null;

					return {
						...offer,
						crop: cropsMap.get(offer.crop_id) || null,
						specialist,
						specialists: Array.isArray(offer.specialist_id)
							? offer.specialist_id.map((id: string) => specialistsMap.get(id)).filter(Boolean)
							: specialist
								? [specialist]
								: [],
					};
				});

				// Only now set both the data and store state with fully enriched data
				setData({ ...offers, data: enrichedOffers });
				setOngoingOffers(enrichedOffers);
			} catch (error) {
				console.error('Error fetching additional details:', error);
				// If enrichment fails, at least set the original data
				setData(offers);
				setOngoingOffers(rawOffers);
			}
		} catch (err) {
			console.error('Error fetching offers:', err);
			setError(err);
		} finally {
			setIsLoading(false);
			isFetchingRef.current = false;
		}
	}, [slug, setOngoingOffers]);

	useEffect(() => {
		if (!isLoading || data) {
			// If we already have data and are not in loading state, don't refetch automatically
			return;
		}
		fetchOffers();
	}, [slug]); // Only depends on slug, not fetchOffers to avoid unnecessary refetches

	return { data, error, isLoading, refetch: fetchOffers };
}

export function useSpecialistsByIds() {
	const { setSpecialists } = useMonitoringOffersStore();
	const [data, setData] = useState<any>(null);
	const [error, setError] = useState<any>(null);
	const [isLoading, setIsLoading] = useState(false);

	const fetchSpecialistsByIds = async (ids: string[]) => {
		setIsLoading(true);
		try {
			const specialists = await getSpecialistsByIds(ids);
			// Extract unique country IDs
			const countryIds = [
				...new Set(specialists.map((specialist: any) => specialist.country).filter(Boolean)),
			] as string[];

			let countriesMap = new Map();

			if (countryIds.length > 0) {
				const countries = await getCountriesByIds(countryIds);
				countriesMap = new Map(countries.map((country: any) => [country.id, country]));
			}

			// Enrich specialists with country details
			const enrichedSpecialists = specialists.map((specialist: any) => ({
				...specialist,
				country: countriesMap.get(specialist.country) || null,
			}));

			setData(enrichedSpecialists);
			setSpecialists(enrichedSpecialists);
			return enrichedSpecialists;
		} catch (err) {
			console.error('Error fetching specialists or countries:', err);
			setError(err);
			return [];
		} finally {
			setIsLoading(false);
		}
	};

	return { data, error, isLoading, fetchSpecialistsByIds };
}

export function useGetCategories(id: any, request_id: any) {
	const { setCategories } = useMonitoringOffersStore();
	const [data, setData] = useState<any>(null);
	const [error, setError] = useState<any>(null);
	const [isLoading, setIsLoading] = useState(false);

	const fetchCategories = useCallback(async () => {
		if (!id) return;

		setIsLoading(true);
		try {
			const categories = await getCategories(id, request_id);
			setData(categories);
			setCategories(categories);
		} catch (err) {
			console.error('Error fetching categories:', err);
			setError(err);
		} finally {
			setIsLoading(false);
		}
	}, [id, setCategories]);

	useEffect(() => {
		fetchCategories();
	}, [fetchCategories]);

	return { data, error, isLoading, refetch: fetchCategories };
}

export function useGetGuideline(id: any) {
	const { setGuideline } = useMonitoringOffersStore();
	const [data, setData] = useState<any>(null);
	const [error, setError] = useState<any>(null);
	const [isLoading, setIsLoading] = useState(false);

	const fetchGuideline = useCallback(async () => {
		if (!id) return;

		setIsLoading(true);
		try {
			const guideline = await getGuideline(id);
			setData(guideline);
			setGuideline(guideline);
		} catch (err) {
			console.error('Error fetching guideline:', err);
			setError(err);
		} finally {
			setIsLoading(false);
		}
	}, [id, setGuideline]);

	useEffect(() => {
		fetchGuideline();
	}, [fetchGuideline]);

	return { data, error, isLoading, refetch: fetchGuideline };
}

export function useGetCompletedOffers() {
	const { setCompletedOffers } = useMonitoringOffersStore();
	const [data, setData] = useState<any>(null);
	const [error, setError] = useState<any>(null);
	const [isLoading, setIsLoading] = useState(true);
	const isFetchingRef = useRef(false);

	const fetchCompletedOffers = useCallback(async () => {
		// Prevent duplicate fetches
		if (isFetchingRef.current) {
			return;
		}

		isFetchingRef.current = true;
		setIsLoading(true);

		try {
			const offers = await getCompletedOffers();

			if (!offers?.data) {
				setData(offers);
				setIsLoading(false);
				isFetchingRef.current = false;
				return;
			}

			// Don't update store yet - wait for all related data to be fetched
			const rawOffers = offers.data;

			const cropIds = [
				...new Set(
					rawOffers
						.map((offer: { crop_id?: string }) => offer.crop_id)
						.filter((id: any): id is string => typeof id === 'string')
				),
			];

			const specialistIds = [
				...new Set(
					rawOffers
						.map((offer: { specialist_id?: string }) => offer.specialist_id)
						.filter((id: any): id is string => typeof id === 'string')
				),
			];

			try {
				const [crops, specialists] = await Promise.all([
					cropIds.length ? getCropsByIds(cropIds) : Promise.resolve([]),
					specialistIds.length ? getSpecialistsByIds(specialistIds) : Promise.resolve([]),
				]);

				const cropsMap = new Map(crops.map((crop: any) => [crop.id, crop]));
				const specialistsMap = new Map(specialists.map((s: any) => [s.user_id, s]));

				const countryIds = [
					...new Set(
						specialists
							.map((s: any) => s.country)
							.filter((id: any): id is string => typeof id === 'string')
					),
				];

				const countries = countryIds.length ? await getCountriesByIds(countryIds) : [];
				const countriesMap = new Map(countries.map((c: any) => [c.id, c]));

				const enrichedSpecialists = specialists.map((specialist: any) => ({
					...specialist,
					country: countriesMap.get(specialist.country) || null,
				}));

				enrichedSpecialists.forEach((s: any) => specialistsMap.set(s.user_id, s));

				const enrichedOffers = rawOffers.map((offer: any) => ({
					...offer,
					crop: cropsMap.get(offer.crop_id) || null,
					specialist: specialistsMap.get(offer.specialist_id) || null,
				}));

				// Only now set both the data and store state with fully enriched data
				setData({ ...offers, data: enrichedOffers });
				setCompletedOffers(enrichedOffers);
			} catch (error) {
				console.error('Error fetching additional details:', error);
				// If enrichment fails, at least set the original data
				setData(offers);
				setCompletedOffers(rawOffers);
			}
		} catch (err) {
			console.error('Error fetching completed offers:', err);
			setError(err);
		} finally {
			setIsLoading(false);
			isFetchingRef.current = false;
		}
	}, [setCompletedOffers]);

	useEffect(() => {
		if (!isLoading || data) {
			// If we already have data and are not in loading state, don't refetch automatically
			return;
		}
		fetchCompletedOffers();
	}, []); // Only run on mount, refetch can be called manually

	return { data, error, isLoading, refetch: fetchCompletedOffers };
}

export function useTopicAttachments(topicId: string, categoryId: string, requestId?: string) {
	const [data, setData] = useState<any>({});
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<any>(null);
	const initialFetchDone = useRef(false);

	// Memoize IDs to prevent unnecessary re-renders
	const memoizedIds = useMemo(
		() => ({
			topicId,
			categoryId,
			requestId,
		}),
		[topicId, categoryId, requestId]
	);

	const fetchAttachments = useCallback(async () => {
		// Skip if we're already loading or missing IDs
		if (isLoading || !memoizedIds.topicId || !memoizedIds.categoryId) return;

		setIsLoading(true);
		try {
			const data = await getTopicAttachments(memoizedIds.topicId, memoizedIds.categoryId, memoizedIds.requestId);
			setData(data?.data || []);
		} catch (err) {
			console.error('Error fetching attachments:', err);
			setError(err);
		} finally {
			setIsLoading(false);
		}
	}, [memoizedIds, isLoading]);

	const removeAttachment = useCallback(
		async (attachmentId: string) => {
			if (!memoizedIds.topicId || !memoizedIds.categoryId || !attachmentId) return;

			try {
				await deleteTopicAttachment(memoizedIds.topicId, memoizedIds.categoryId, attachmentId, memoizedIds.requestId);
				// Don't fetch again, just update the state directly
				setData((prev: any) => {
					if (prev?.attachments) {
						return {
							...prev,
							attachments: prev.attachments.filter((item: any) => item.id !== attachmentId),
						};
					}
					return prev;
				});
				return true;
			} catch (err) {
				console.error('Error removing attachment:', err);
				setError(err);
				throw err;
			}
		},
		[memoizedIds]
	);

	useEffect(() => {
		if (memoizedIds.topicId && memoizedIds.categoryId && !initialFetchDone.current) {
			fetchAttachments();
			initialFetchDone.current = true;
		}

		// Reset the flag when IDs change
		return () => {
			if (
				initialFetchDone.current &&
				(topicId !== memoizedIds.topicId || categoryId !== memoizedIds.categoryId)
			) {
				initialFetchDone.current = false;
			}
		};
	}, [memoizedIds.topicId, memoizedIds.categoryId, fetchAttachments]);

	return {
		data,
		isLoading,
		error,
		refreshAttachments: fetchAttachments,
		removeAttachment,
	};
}

/**
 * Hook to fetch specialist offers with enriched data including farmers
 */
export function useGetOffersSpecialist() {
	const { setOngoingOffers } = useMonitoringOffersStore();
	const [data, setData] = useState<any>(null);
	const [error, setError] = useState<any>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isFetching, setIsFetching] = useState(false);

	const fetchOffersSpecialist = useCallback(async () => {
		setIsFetching(true);
		try {
			const offers = await getOffersSpecialist('ongoing-project');

			if (!offers?.data) {
				setData(offers);
				setIsFetching(false);
				setIsLoading(false);
				return;
			}

			// Don't update store yet - wait for all related data to be fetched
			const rawOffers = offers.data;

			const cropIds = [
				...new Set(
					rawOffers
						.map((offer: { crop_id?: string }) => offer.crop_id)
						.filter((id: any): id is string => typeof id === 'string')
				),
			];

			const specialistIds = [
				...new Set(
					rawOffers
						.map((offer: { specialist_id?: string }) => offer.specialist_id)
						.filter((id: any): id is string => typeof id === 'string')
				),
			];

			// Get unique farmer IDs across all offers
			const farmerIds = Array.from(
				new Set(
					rawOffers
						.map((offer: { farmer_id?: string }) => offer.farmer_id)
						.filter((id: any): id is string => typeof id === 'string')
				)
			) as string[];

			try {
				const [crops, specialists] = await Promise.all([
					cropIds.length ? getCropsByIds(cropIds) : Promise.resolve([]),
					specialistIds.length ? getSpecialistsByIds(specialistIds) : Promise.resolve([]),
				]);

				// Create maps for efficient lookups
				const cropsMap = new Map(crops.map((crop: any) => [crop.id, crop]));
				const specialistsMap = new Map(specialists.map((s: any) => [s.user_id, s]));

				// Get countries for specialists
				const countryIds = [
					...new Set(
						specialists
							.map((s: any) => s.country)
							.filter((id: any): id is string => typeof id === 'string')
					),
				];

				const countries = countryIds.length ? await getCountriesByIds(countryIds) : [];
				const countriesMap = new Map(countries.map((c: any) => [c.id, c]));

				// Enrich specialists with country data
				const enrichedSpecialists = specialists.map((specialist: any) => ({
					...specialist,
					country: countriesMap.get(specialist.country) || null,
				}));

				enrichedSpecialists.forEach((s: any) => specialistsMap.set(s.user_id, s));

				// Prepare to fetch farmer data for each offer
				const farmersPromises = farmerIds.map(async (farmerId: string) => {
					try {
						const farmerResponse = await getFarmerDataById(farmerId);
						return {
							id: farmerId,
							farmer: farmerResponse.data,
						};
					} catch (error) {
						console.error(`Error fetching farmer data for ${farmerId}:`, error);
						return { id: farmerId, farmer: null };
					}
				});

				// Fetch all farmer data in parallel
				const farmersResults = await Promise.all(farmersPromises);
				const farmersMap = new Map(farmersResults.map((result) => [result.id, result.farmer]));

				// Create enriched offers with all the fetched data
				const enrichedOffers = rawOffers.map((offer: any) => ({
					...offer,
					crop: cropsMap.get(offer.crop_id) || null,
					specialist: specialistsMap.get(offer.specialist_id) || null,
					farmer: farmersMap.get(offer.farmer_id) || null,
				}));

				// Only now set both the data and store state with fully enriched data
				setData({ ...offers, data: enrichedOffers });
				setOngoingOffers(enrichedOffers);
			} catch (error) {
				console.error('Error fetching additional details:', error);
				// If enrichment fails, at least set the original data
				setData(offers);
				setOngoingOffers(rawOffers);
			}
		} catch (err) {
			console.error('Error fetching specialist offers:', err);
			setError(err);
		} finally {
			setIsLoading(false);
			setIsFetching(false);
		}
	}, [setOngoingOffers]);

	useEffect(() => {
		setIsLoading(true);
		fetchOffersSpecialist();
	}, [fetchOffersSpecialist]);

	return { data, error, isLoading: isLoading || isFetching, refetch: fetchOffersSpecialist };
}
